import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { chamarApi } from '@/nucleo/auth/cliente-api';
import { naoInsistirEmRecusa } from '@/nucleo/naoInsistirEmRecusa';

import type {
  DetalheDoUsuario,
  EntregaDoEmail,
  EntradaDoHistoricoAdministrativo,
  EntradaDoHistoricoDeAcesso,
  ListaDeUsuarios,
  OpcoesDoModulo,
  Pagina,
  ResultadoDaAcao,
} from './contrato';

/**
 * Camada de dados do módulo de usuários. Tudo da API
 * (`bff/v1/admin-usuarios`); nenhuma ação grava na tela antes da resposta, e
 * toda ação, depois dela, recarrega o que mudou.
 */
export const CAMINHO_DA_API = '/bff/v1/admin-usuarios';

export interface FiltrosDaLista {
  busca: string;
  perfil: string;
  situacao: string;
  segundoFator: string;
  pagina: number;
  tamanho: number;
}

export const CHAVES = {
  tudo: ['admin', 'usuarios'] as const,
  opcoes: ['admin', 'usuarios', 'opcoes'] as const,
  lista: (f: FiltrosDaLista) => ['admin', 'usuarios', 'lista', f] as const,
  detalhe: (id: string) => ['admin', 'usuarios', 'detalhe', id] as const,
  acessos: (id: string, pagina: number) => ['admin', 'usuarios', 'acessos', id, pagina] as const,
  historico: (id: string, pagina: number) =>
    ['admin', 'usuarios', 'historico', id, pagina] as const,
};

export function consultaDaLista(f: FiltrosDaLista): string {
  const p = new URLSearchParams({ pagina: String(f.pagina), tamanho: String(f.tamanho) });
  if (f.busca.trim()) p.set('busca', f.busca.trim());
  if (f.perfil) p.set('perfil', f.perfil);
  if (f.situacao) p.set('situacao', f.situacao);
  if (f.segundoFator) p.set('segundoFator', f.segundoFator);
  return `${CAMINHO_DA_API}/usuarios?${p.toString()}`;
}

export function useOpcoes() {
  return useQuery({
    queryKey: CHAVES.opcoes,
    queryFn: ({ signal }) =>
      chamarApi<OpcoesDoModulo>(`${CAMINHO_DA_API}/opcoes`, { sinal: signal }),
    staleTime: 5 * 60_000,
    retry: naoInsistirEmRecusa,
  });
}

export function useListaDeUsuarios(filtros: FiltrosDaLista) {
  return useQuery({
    queryKey: CHAVES.lista(filtros),
    queryFn: ({ signal }) =>
      chamarApi<ListaDeUsuarios>(consultaDaLista(filtros), { sinal: signal }),
    placeholderData: keepPreviousData,
    retry: naoInsistirEmRecusa,
  });
}

export function useDetalheDoUsuario(id: string) {
  return useQuery({
    queryKey: CHAVES.detalhe(id),
    queryFn: ({ signal }) =>
      chamarApi<DetalheDoUsuario>(`${CAMINHO_DA_API}/usuarios/${encodeURIComponent(id)}`, {
        sinal: signal,
      }),
    retry: naoInsistirEmRecusa,
  });
}

export function useHistoricoDeAcessos(id: string, pagina: number, ativo: boolean) {
  return useQuery({
    queryKey: CHAVES.acessos(id, pagina),
    queryFn: ({ signal }) =>
      chamarApi<Pagina<EntradaDoHistoricoDeAcesso>>(
        `${CAMINHO_DA_API}/usuarios/${encodeURIComponent(id)}/acessos?pagina=${pagina}&tamanho=20`,
        { sinal: signal },
      ),
    enabled: ativo,
    placeholderData: keepPreviousData,
    retry: naoInsistirEmRecusa,
  });
}

export function useHistoricoAdministrativo(id: string, pagina: number, ativo: boolean) {
  return useQuery({
    queryKey: CHAVES.historico(id, pagina),
    queryFn: ({ signal }) =>
      chamarApi<Pagina<EntradaDoHistoricoAdministrativo>>(
        `${CAMINHO_DA_API}/usuarios/${encodeURIComponent(id)}/historico?pagina=${pagina}&tamanho=20`,
        { sinal: signal },
      ),
    enabled: ativo,
    placeholderData: keepPreviousData,
    retry: naoInsistirEmRecusa,
  });
}

/** Ações sobre a conta, pelo nome da rota da API. */
export type AcaoDaConta =
  | 'bloquear'
  | 'desbloquear'
  | 'revogar-sessoes'
  | 'link-troca-senha'
  | 'exigir-segundo-fator'
  | 'editar';

export function useAcaoDaConta(id: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ acao, corpo }: { acao: AcaoDaConta; corpo: Record<string, unknown> }) =>
      chamarApi<ResultadoDaAcao>(`${CAMINHO_DA_API}/usuarios/${encodeURIComponent(id)}/${acao}`, {
        metodo: 'POST',
        corpo,
      }),
    // Também na falha: uma ação pode ter feito parte do que pediu (o segundo
    // fator apagado, as sessões não) e a tela precisa mostrar o estado real.
    onSettled: () => cliente.invalidateQueries({ queryKey: CHAVES.tudo }),
  });
}

export function useAcaoDoConvite() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({
      conviteId,
      acao,
      corpo,
    }: {
      conviteId: string;
      acao: 'reenviar' | 'cancelar';
      corpo: Record<string, unknown>;
    }) =>
      chamarApi<ResultadoDaAcao>(
        `${CAMINHO_DA_API}/convites/${encodeURIComponent(conviteId)}/${acao}`,
        { metodo: 'POST', corpo },
      ),
    // Sem recarregar já: depois de cancelar ou reenviar, a tela sai desta
    // pessoa (a identidade antiga deixa de existir) e a próxima busca é nova.
    onSuccess: () => cliente.invalidateQueries({ queryKey: CHAVES.tudo, refetchType: 'none' }),
  });
}

/** Para onde iria um e-mail para este endereço neste ambiente (o convite avisa antes). */
export function useEntrega(email: string) {
  const valido = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  return useQuery({
    queryKey: ['admin', 'usuarios', 'entrega', email] as const,
    queryFn: ({ signal }) =>
      chamarApi<{ entrega: EntregaDoEmail }>(
        `${CAMINHO_DA_API}/entrega?email=${encodeURIComponent(email)}`,
        { sinal: signal },
      ),
    enabled: valido,
    staleTime: 5 * 60_000,
    retry: naoInsistirEmRecusa,
  });
}

export function useConvidar() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: Record<string, unknown>) =>
      chamarApi<ResultadoDaAcao>(`${CAMINHO_DA_API}/convites`, { metodo: 'POST', corpo }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: CHAVES.tudo }),
  });
}

/** Chave de idempotência: a mesma enquanto a pessoa repete a MESMA ação. */
export function novaChave(): string {
  return crypto.randomUUID();
}
